import { useState } from 'react';
import {
  Target,
  MessageCircle,
  Home,
  Sparkles,
  MoreHorizontal,
  Users,
  Trophy,
  ShoppingBag,
  User,
  Settings,
  Info,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

const bottomNavItems = [
  { id: 'quests', label: 'Quests', icon: Target, color: 'text-orange-400' },
  { id: 'chat', label: 'Chat', icon: MessageCircle, color: 'text-green-400' },
  { id: 'learn', label: 'Learn', icon: Home, color: 'text-orange-500' },
  { id: 'curiosity', label: 'Curiosity', icon: Sparkles, color: 'text-purple-400' },
  { id: 'more', label: 'More', icon: MoreHorizontal, color: 'text-gray-500' },
];

const moreMenuItems = [
  { id: 'community', label: 'Community', icon: Users, color: 'text-amber-400' },
  { id: 'leaderboard', label: 'Leaderboard', icon: Trophy, color: 'text-yellow-400' },
  { id: 'shop', label: 'Shop', icon: ShoppingBag, color: 'text-red-400' },
  { id: 'profile', label: 'Profile', icon: User, color: 'text-gray-400' },
  { id: 'settings', label: 'Settings', icon: Settings, color: 'text-gray-400' },
  { id: 'about', label: 'About', icon: Info, color: 'text-teal-400' },
];

export default function MobileBottomNav({ activeSection, onSectionChange, onNavigate = () => {} }) {
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const handleItemClick = (itemId) => {
    if (itemId === 'more') {
      setShowMoreMenu(true);
      return;
    }

    onSectionChange(itemId);
    onNavigate();
  };

  const handleMoreItemClick = (itemId) => {
    onSectionChange(itemId);
    setShowMoreMenu(false);
    onNavigate();
  };

  // Reset scroll position when More menu opens
  const handleMoreMenuOpen = (open) => {
    setShowMoreMenu(open);
    if (open) {
      // Small delay to ensure the sheet is rendered before scrolling
      setTimeout(() => {
        const sheetContent = document.querySelector('[data-radix-dialog-content]');
        if (sheetContent) {
          sheetContent.scrollTop = 0;
        }
      }, 100);
    }
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[hsl(var(--card-bg))]/95 backdrop-blur-md border-t border-[hsl(var(--card-border))] pb-safe">
        <div className="flex items-center justify-between px-2 py-1">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center p-1.5 rounded-lg transition-all duration-200 relative group',
                  isActive
                    ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'
                    : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50'
                )}
              >
                <Icon className={cn('w-5 h-5 mb-0.5', item.color)} />
                <span className={cn(
                  'text-[9px] font-medium transition-all duration-200',
                  isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                )}>
                  {item.label}
                </span>

                {/* Active indicator */}
                {isActive && (
                  <div className="absolute -top-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[hsl(var(--primary))] rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* More Menu Sheet */}
      <Sheet open={showMoreMenu} onOpenChange={handleMoreMenuOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-xl max-h-[80vh] bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]"
        >
          <SheetHeader className="text-left pb-4">
            <SheetTitle className="text-[hsl(var(--muted-foreground))] flex items-center gap-2 text-sm">
              <MoreHorizontal className="w-5 h-5" />
              More Options
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-2" role="menu" aria-label="More options menu">
            {moreMenuItems.map((item, index) => {
              const Icon = item.icon;
              // Don't highlight any item as active in the More menu to prevent auto-scrolling
              const isActive = false;

              return (
                <button
                  key={item.id}
                  onClick={() => handleMoreItemClick(item.id)}
                  className={cn(
                    'w-full flex items-center gap-4 p-4 rounded-lg transition-all duration-200 text-left',
                    isActive
                      ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20'
                      : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                  )}
                  role="menuitem"
                  tabIndex={index === 0 ? 0 : -1} // Only first item is focusable by default
                >
                  <Icon className={cn('w-5 h-5 flex-shrink-0', item.color)} />
                  <span className="font-medium text-[hsl(var(--muted-foreground))] text-sm">{item.label}</span>
                  {isActive && (
                    <div className="ml-auto w-2 h-2 bg-[hsl(var(--primary))] rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}